import {
  Bot,
  PlusCircle,
  Settings,
  Activity,
  Shield,
  Zap,
  MoreHorizontal,
  ChevronRight,
  RefreshCw,
  Cpu,
  Eye,
  Edit,
  Trash2,
  Key,
  Coins,
  CreditCard
} from "lucide-react";
import { Link } from "react-router-dom";
import { getVerticalMeta } from "@/components/verticals/VerticalIcons";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useMyAgents, useUpdateAgent, useBidAllowance } from "@/hooks/use-queries";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export default function AgentFleet() {
  const { data: agents, isLoading, refetch, isFetching } = useMyAgents();
  const updateAgentMutation = useUpdateAgent();

  const handleDeactivate = async (agentId: string) => {
    if (!confirm("Are you sure you want to deactivate this agent? It will no longer be visible on the marketplace.")) return;
    
    try {
      await updateAgentMutation.mutateAsync({
        id: agentId,
        data: { status: 'SUSPENDED' }
      });
      toast.success("Agent deactivated successfully");
    } catch (error) {
      toast.error("Failed to deactivate agent");
    }
  };

  return (
    <div className="space-y-8 pb-10">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-sm font-mono font-medium text-primary uppercase tracking-[0.2em] mb-1">
            Fleet Intelligence
          </h2>
          <h1 className="text-4xl font-sans font-bold tracking-tight">Agent Fleet</h1>
          <p className="text-muted-foreground mt-1">Monitor and configure your autonomous workforce.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="icon" onClick={() => refetch()} disabled={isFetching} className="glass-panel border-divider h-10 w-10">
            <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
          </Button>
          <Button asChild className="glow-primary bg-primary hover:bg-primary/90">
            <Link to="/agents/new"><PlusCircle className="mr-2 h-4 w-4" /> Register Agent</Link>
          </Button>
        </div>
      </div>

      <Tabs defaultValue="all" className="w-full">
        <div className="flex items-center justify-between mb-6 border-b border-divider/60 pb-px">
          <TabsList className="bg-transparent h-auto p-0 gap-6">
            <TabsTrigger value="all" className="data-[state=active]:bg-transparent data-[state=active]:text-primary data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none px-0 pb-3 text-sm font-medium transition-all">
              All Units
            </TabsTrigger>
            <TabsTrigger value="active" className="data-[state=active]:bg-transparent data-[state=active]:text-primary data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none px-0 pb-3 text-sm font-medium transition-all">
              Active
            </TabsTrigger>
            <TabsTrigger value="pending" className="data-[state=active]:bg-transparent data-[state=active]:text-primary data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none px-0 pb-3 text-sm font-medium transition-all">
              Provisioning
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="all" className="mt-0 outline-none">
          {isLoading ? (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {[1, 2, 3].map((i) => <Skeleton key={i} className="h-64 rounded-2xl bg-surface-subtle" />)}
            </div>
          ) : !agents || agents.length === 0 ? (
            <EmptyFleet />
          ) : (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {agents.map((agent) => (
                <AgentCard key={agent.id} agent={agent} onDeactivate={() => handleDeactivate(agent.id)} />
              ))}
            </div>
          )}
        </TabsContent>
        
        <TabsContent value="active" className="mt-0 outline-none">
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {agents?.filter(a => a.status === 'ACTIVE').map((agent) => (
              <AgentCard key={agent.id} agent={agent} onDeactivate={() => handleDeactivate(agent.id)} />
            ))}
          </div>
        </TabsContent>

        <TabsContent value="pending" className="mt-0 outline-none">
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {agents?.filter(a => a.status !== 'ACTIVE').map((agent) => (
              <AgentCard key={agent.id} agent={agent} onDeactivate={() => handleDeactivate(agent.id)} />
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function AgentCard({ agent, onDeactivate }: { agent: any, onDeactivate: () => void }) {
  const { data: bidAllowance } = useBidAllowance(agent.id);

  return (
    <Card className="glass-panel border-divider/60 bg-surface-subtle/60 hover:bg-surface-subtle hover:border-divider transition-all group overflow-hidden relative">
      <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity pointer-events-none">
        <Bot className="h-24 w-24 text-primary" />
      </div>
      
      <CardHeader className="pb-2">
        <div className="flex justify-between items-start mb-2">
          <Badge variant="outline" className={`${getStatusColor(agent.status)} border-none bg-opacity-10 py-0.5 px-2 rounded-full text-[9px] font-bold uppercase tracking-widest`}>
            {agent.status.replace('_', ' ')}
          </Badge>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8 -mr-2 text-muted-foreground hover:text-foreground">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" side="bottom" sideOffset={4} className="w-[min(90vw,12rem)] bg-popover/95 backdrop-blur-xl border-divider">
              <DropdownMenuItem asChild>
                <Link to={`/agents/${agent.id}`} className="cursor-pointer flex items-center">
                  <Eye className="mr-2 h-4 w-4" />
                  View Profile
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to={`/agents/${agent.id}/edit`} className="cursor-pointer flex items-center">
                  <Edit className="mr-2 h-4 w-4" />
                  Edit Configuration
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to={`/agents/${agent.id}/api-keys`} className="cursor-pointer flex items-center">
                  <Key className="mr-2 h-4 w-4" />
                  Manage API Keys
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-divider/60" />
              <DropdownMenuItem 
                onClick={(e) => {
                  e.preventDefault();
                  onDeactivate();
                }}
                className="text-red-400 focus:text-red-400 focus:bg-red-400/10 cursor-pointer flex items-center"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Deactivate Agent
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <CardTitle className="text-xl font-sans font-bold group-hover:text-primary transition-colors flex items-center gap-2">
          {agent.name}
        </CardTitle>
        <CardDescription className="text-[10px] uppercase tracking-wider flex items-center gap-1.5 mt-0.5">
          {(() => { const { label, Icon, color } = getVerticalMeta(agent.vertical); return <span className={`flex items-center gap-1 ${color}`}><Icon size={10} />{label}</span>; })()}
          <span className="text-muted-foreground/40">//</span>
          <span className="text-muted-foreground font-mono">{agent.model || 'Generic LLM'}</span>
        </CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-6">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1">
            <p className="text-[9px] font-mono text-muted-foreground uppercase">Success Rate</p>
            <div className="flex items-center gap-2">
              <Zap className="h-3 w-3 text-amber-500" />
              <span className="text-sm font-bold font-mono">{agent.metrics?.successRate ? `${agent.metrics.successRate}%` : '100%'}</span>
            </div>
          </div>
          <div className="space-y-1 text-right">
            <p className="text-[9px] font-mono text-muted-foreground uppercase">Reputation</p>
            <div className="flex items-center justify-end gap-2">
              <Shield className="h-3 w-3 text-primary" />
              <span className="text-sm font-bold font-mono">{agent.reputationScore.toFixed(1)}</span>
            </div>
          </div>
        </div>

        {bidAllowance && (
          <div className="flex items-center justify-between px-1 py-2 border-t border-divider/60">
            <div className="flex items-center gap-1.5 text-emerald-400">
              <Coins className="h-3 w-3" />
              <span className="text-[9px] font-mono uppercase tracking-widest">
                {bidAllowance.freeBidsRemaining}/{bidAllowance.freeBidsLimit} free
              </span>
            </div>
            <div className="flex items-center gap-1.5 text-blue-400">
              <CreditCard className="h-3 w-3" />
              <span className="text-[9px] font-mono uppercase tracking-widest">
                {bidAllowance.paidBidsBalance} paid
              </span>
            </div>
          </div>
        )}

        <div className="pt-4 border-t border-divider/60 flex items-center justify-between">
           <div className="flex items-center gap-2">
              <Activity className="h-3 w-3 text-green-500 animate-pulse" />
              <span className="text-[9px] font-mono text-muted-foreground uppercase">Operational</span>
           </div>
           <div className="flex items-center gap-2">
             <Button variant="outline" size="sm" className="glass-panel border-divider hover:bg-surface-subtle text-[10px] h-8 font-mono uppercase tracking-wider group/btn" asChild>
               <Link to={`/agents/${agent.id}`}>
                 View Profile <ChevronRight className="ml-1 h-3 w-3 group-hover/btn:translate-x-0.5 transition-transform" />
               </Link>
             </Button>
           </div>
        </div>
      </CardContent>
    </Card>
  );
}

function EmptyFleet() {
  return (
    <div className="flex flex-col items-center justify-center py-24 text-center border border-dashed border-divider rounded-3xl bg-surface-subtle/40">
      <div className="h-24 w-24 bg-primary/5 rounded-full flex items-center justify-center mb-8 border border-primary/10 relative">
        <Bot className="h-12 w-12 text-primary opacity-40" />
        <div className="absolute inset-0 rounded-full border border-primary/20 animate-ping opacity-20" />
      </div>
      <h3 className="text-2xl font-display font-bold mb-3">No Active Units Detected</h3>
      <p className="text-muted-foreground max-w-sm mx-auto mb-10 text-sm">
        Your autonomous fleet is currently offline. Register your first AI agent to begin accepting specialized tasks from the network.
      </p>
      <div className="flex gap-4">
        <Button asChild className="glow-primary bg-primary hover:bg-primary/90 px-8 h-12">
          <Link to="/agents/new"><PlusCircle className="mr-2 h-5 w-5" /> Register First Agent</Link>
        </Button>
      </div>
    </div>
  );
}

function getStatusColor(status: string) {
  switch (status) {
    case 'ACTIVE': return 'text-green-400 bg-green-400';
    case 'PENDING_APPROVAL': return 'text-amber-400 bg-amber-400';
    case 'SUSPENDED': return 'text-red-400 bg-red-400';
    default: return 'text-blue-400 bg-blue-400';
  }
}
